import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TrendingUp, Award, Package, ArrowLeft, User, AlertCircle, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { charactersService, adminService } from '../services';
import PageContainer from '../components/PageContainer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/Loading';
import AddRelicModal from '../components/AddRelicModal';
import CompleteQuestModal from '../components/CompleteQuestModal';
import EditCharacterModal from '../components/EditCharacterModal';
import AddItemModal from '../components/AddItemModal';
import EditItemModal from '../components/EditItemModal';
import {getCharacterStatsText, getClassTypeIcon, getClassTypeTranslation} from "../utils/characterTranslation";
import {formatDate, formatRelativeTime} from '../utils/dateUtils';

const CharacterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal states
  const [showAddRelicModal, setShowAddRelicModal] = useState(false);
  const [showCompleteQuestModal, setShowCompleteQuestModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [showLevelUpModal, setShowLevelUpModal] = useState(false);
  const [showLevelUpAnimation, setShowLevelUpAnimation] = useState(false);
  const [levelUpData, setLevelUpData] = useState(null);
  const [selectedCharacterItem, setSelectedCharacterItem] = useState(null);
  const [itemTypeFilter, setItemTypeFilter] = useState(null);
  const [availableRelics, setAvailableRelics] = useState([]);
  const [availableQuests, setAvailableQuests] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [addingRelic, setAddingRelic] = useState(false);
  const [completingQuest, setCompletingQuest] = useState(false);
  const [editing, setEditing] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [editingItem, setEditingItem] = useState(false);

  useEffect(() => {
    loadCharacter();
    loadAvailableData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCharacter = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await charactersService.getCharacter(id);
      setCharacter(response);
    } catch (err) {
      console.error('Error loading character:', err);
      setError(err.response?.data?.error || 'Erro ao carregar personagem');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableData = async () => {
    try {
      const [relicsResponse, questsResponse, itemsResponse] = await Promise.all([
        adminService.getRelics(),
        adminService.getQuests(),
        adminService.getItems()
      ]);
      setAvailableRelics(relicsResponse.data?.relics || []);
      setAvailableQuests(questsResponse.data?.quests || []);
      setAvailableItems(itemsResponse.data?.items || []);
    } catch (err) {
      console.error('Error loading available data:', err);
      // Not critical, just show empty lists
    }
  };

  const organizeItems = (items) => {
    const organized = {
      weapon: null,
      armor: null,
      shield: null,
      accessories: {
        boots: null,
        gloves: null,
        bracelet: null
      },
      other: []
    };

    items?.forEach(charItem => {
      const item = charItem.item;
      if (item.type === 'WEAPON') {
        organized.weapon = charItem;
      } else if (item.type === 'ARMOR') {
        organized.armor = charItem;
      } else if (item.type === 'SHIELD') {
        organized.shield = charItem;
      } else if (item.type === 'BOOTS') {
        organized.accessories.boots = charItem;
      } else if (item.type === 'GLOVES') {
        organized.accessories.gloves = charItem;
      } else if (item.type === 'BRACELET') {
        organized.accessories.bracelet = charItem;
      } else if (item.type === 'ACCESSORY') {
        // Para acessórios genéricos, colocar em "outros"
        organized.other.push(charItem);
      } else {
        organized.other.push(charItem);
      }
    });

    return organized;
  };

  const handleAddRelic = async (relicData) => {
    setAddingRelic(true);
    setError('');
    
    try {
      await charactersService.addRelic(id, relicData);
      loadCharacter();
      setShowAddRelicModal(false);
    } catch (err) {
      console.error('Error adding relic:', err);
      setError(err.response?.data?.error || 'Erro ao adicionar relíquia');
    } finally {
      setAddingRelic(false);
    }
  };

  const handleCompleteQuest = async (questData) => {
    setCompletingQuest(true);
    setError('');
    
    try {
      await charactersService.completeQuest(id, questData);
      loadCharacter();
      setShowCompleteQuestModal(false);
    } catch (err) {
      console.error('Error completing quest:', err);
      setError(err.response?.data?.error || 'Erro ao completar quest');
    } finally {
      setCompletingQuest(false);
    }
  };

  const canManageCharacter = user && (user.role === 'ADMIN' || character?.userId === user.id);

  const handleEditCharacter = () => {
    setShowEditModal(true);
  };

  const handleUpdateCharacter = async (characterData) => {
    setEditing(true);
    setError('');
    
    try {
      await charactersService.updateCharacter(id, characterData);
      loadCharacter();
      setShowEditModal(false);
    } catch (err) {
      console.error('Error updating character:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar personagem');
    } finally {
      setEditing(false);
    }
  };

  const handleAddItem = async (itemData) => {
    setAddingItem(true);
    setError('');
    
    try {
      await charactersService.addItem(id, itemData);
      loadCharacter();
      setShowAddItemModal(false);
      setItemTypeFilter(null);
    } catch (err) {
      console.error('Error adding item:', err);
      setError(err.response?.data?.error || 'Erro ao adicionar item');
    } finally {
      setAddingItem(false);
    }
  };

  const openAddItemModal = (itemType = null) => {
    setItemTypeFilter(itemType);
    setShowAddItemModal(true);
  };

  const closeAddItemModal = () => {
    setShowAddItemModal(false);
    setItemTypeFilter(null);
  };

  const handleEditItem = (characterItem) => {
    setSelectedCharacterItem(characterItem);
    setShowEditItemModal(true);
  };

  const handleUpdateItem = async (itemData) => {
    setEditingItem(true);
    setError('');
    
    try {
      // Assuming there's an API to update character items
      await charactersService.updateCharacterItem(id, selectedCharacterItem.id, itemData);
      loadCharacter();
      setShowEditItemModal(false);
      setSelectedCharacterItem(null);
    } catch (err) {
      console.error('Error updating item:', err);
      setError(err.response?.data?.error || 'Erro ao atualizar item');
    } finally {
      setEditingItem(false);
    }
  };

  const handleRemoveItem = async () => {
    setEditingItem(true);
    setError('');
    
    try {
      // Assuming there's an API to remove character items
      await charactersService.removeCharacterItem(id, selectedCharacterItem.id);
      loadCharacter();
      setShowEditItemModal(false);
      setSelectedCharacterItem(null);
    } catch (err) {
      console.error('Error removing item:', err);
      setError(err.response?.data?.error || 'Erro ao remover item');
    } finally {
      setEditingItem(false);
    }
  };

  const handleLevelUp = async () => {
    try {
      setLoading(true);
      const response = await charactersService.levelUpCharacter(id);
      setLevelUpData(response.levelUp);
      setShowLevelUpModal(false);
      setShowLevelUpAnimation(true);
      
      // Esconder animação após 3 segundos
      setTimeout(() => {
        setShowLevelUpAnimation(false);
        loadCharacter();
      }, 3000);
    } catch (err) {
      console.error('Error leveling up:', err);
      setError(err.response?.data?.error || 'Erro ao fazer level up');
      setShowLevelUpModal(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageContainer title="Detalhes do Personagem" description="Carregando...">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  if (error || !character) {
    return (
      <PageContainer 
        title="Erro"
        actions={
          <Button variant="outline" onClick={() => navigate('/app/characters')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        }
      >
        <Card variant="glass" className="p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {error || 'Personagem não encontrado'}
          </h3>
          <p className="text-gray-400 mb-6">
            Não foi possível carregar os detalhes do personagem.
          </p>
          <Button onClick={() => navigate('/app/characters')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Personagens
          </Button>
        </Card>
      </PageContainer>
    );
  }

  const organizedItems = organizeItems(character.items);

  return (
    <PageContainer
      title={character.name}
      description={`${getClassTypeTranslation(character.class)} - Nível ${character.level}`}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/app/characters')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          {character.userId === user?.id && (
            <Button 
              variant="primary" 
              onClick={() => setShowLevelUpModal(true)}
              className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Level Up
            </Button>
          )}
          {(user?.role === 'ADMIN' || character.userId === user?.id) && (
            <Button onClick={handleEditCharacter}>
              Editar
            </Button>
          )}
        </div>
      }
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card variant="glass" className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent"></div>
            <div className="relative p-6 text-center">
              <TrendingUp className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
              <p className="text-sm text-gray-400 mb-1">Nível</p>
              <h3 className="text-3xl font-bold text-white">{character.level}</h3>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <Card variant="glass" className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-transparent"></div>
            <div className="relative p-6 text-center">
              <Award className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-sm text-gray-400 mb-1">Pontos</p>
              <h3 className="text-2xl font-bold text-white">{character.totalPoints?.toLocaleString()}</h3>
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <Card variant="glass" className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent"></div>
            <div className="relative p-6 text-center">
              <Package className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-sm text-gray-400 mb-1">Itens</p>
              <h3 className="text-2xl font-bold text-white">{character.items?.length || 0}</h3>
            </div>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Character Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="lg:col-span-1"
        >
          <Card variant="glass">
            <div className="p-6 text-center">
              <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-5xl">
                {getClassTypeIcon(character.class)}
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{character.name}</h2>
              <p className="text-lg text-cyan-400 mb-4">{getClassTypeTranslation(character.class)}</p>
              
              {character.user && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-4">
                  <User className="w-4 h-4" />
                  <span>{character.user.name}</span>
                </div>
              )}

              <div className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${
                character.status === 'ACTIVE' 
                  ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                  : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
              }`}>
                {getCharacterStatsText(character.status)}
              </div>

              <div className="mt-6 space-y-4 bg-white/5 rounded-xl p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Level
                  </span>
                  <span className="font-semibold text-white text-lg">{character.level}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Pontos Totais
                  </span>
                  <span className="font-semibold text-amber-400">{character.totalPoints?.toLocaleString()}</span>
                </div>
                <div className="border-t border-white/10 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Última Atualização
                    </span>
                    <div className="text-right">
                      <div className="text-sm font-medium text-cyan-400">{formatRelativeTime(character.updatedAt)}</div>
                      <div className="text-xs text-gray-500">{formatDate(character.updatedAt)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Equipment */}
        <div className="lg:col-span-2">
          <div className="space-y-6">
            {/* Arma e Armadura */}
            <Card variant="glass">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">⚔️ Equipamentos Principais</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Arma */}
                  <div className="border border-white/10 rounded-lg p-4 bg-slate-700/30">
                    <h3 className="font-medium text-white mb-2">🗡️ Arma</h3>
                    {organizedItems.weapon ? (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-white">{organizedItems.weapon.item.name}</span>
                        </div>
                        <p className="text-sm text-gray-400">{organizedItems.weapon.item.description}</p>
                        <div className="flex justify-between items-center text-sm text-purple-400">
                          <span>Level: +{organizedItems.weapon.enhancement}</span>
                        </div>
                        {canManageCharacter && (
                          <div className="mt-2">
                            <Button
                              size="xs"
                              variant="secondary"
                              onClick={() => handleEditItem(organizedItems.weapon)}
                            >
                              Editar
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-gray-400 text-sm mb-2">Nenhuma arma equipada</p>
                        {canManageCharacter && (
                          <Button
                            size="xs"
                            onClick={() => openAddItemModal('WEAPON')}
                          >
                            Adicionar Arma
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Armadura */}
                  <div className="border border-white/10 rounded-lg p-4 bg-slate-700/30">
                    <h3 className="font-medium text-white mb-2">🛡️ Armadura/Roupão</h3>
                    {organizedItems.armor ? (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-white">{organizedItems.armor.item.name}</span>
                        </div>
                        <p className="text-sm text-gray-400">{organizedItems.armor.item.description}</p>
                        <div className="flex justify-between items-center text-sm text-purple-400">
                          <span>Level: +{organizedItems.armor.enhancement}</span>
                        </div>
                        {canManageCharacter && (
                          <div className="mt-2">
                            <Button
                              size="xs"
                              variant="secondary"
                              onClick={() => handleEditItem(organizedItems.armor)}
                            >
                              Editar
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-gray-400 text-sm mb-2">Nenhuma armadura/roupão equipado</p>
                        {canManageCharacter && (
                          <Button
                            size="xs"
                            onClick={() => openAddItemModal('ARMOR')}
                          >
                            Adicionar
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Escudo */}
                  <div className="border border-white/10 rounded-lg p-4 bg-slate-700/30">
                    <h3 className="font-medium text-white mb-2">🛡️ Escudo</h3>
                    {organizedItems.shield ? (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-white">{organizedItems.shield.item.name}</span>
                        </div>
                        <p className="text-sm text-gray-400">{organizedItems.shield.item.description}</p>
                        <div className="flex justify-between items-center text-sm text-purple-400">
                          <span>Level: +{organizedItems.shield.enhancement}</span>
                        </div>
                        {canManageCharacter && (
                          <div className="mt-2">
                            <Button
                              size="xs"
                              variant="secondary"
                              onClick={() => handleEditItem(organizedItems.shield)}
                            >
                              Editar
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-gray-400 text-sm mb-2">Nenhum escudo equipado</p>
                        {canManageCharacter && (
                          <Button
                            size="xs"
                            onClick={() => openAddItemModal('SHIELD')}
                          >
                            Adicionar Escudo
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Set de Acessórios */}
            <Card variant="glass">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">💎 Set</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Botas */}
                  <div className="border border-white/10 rounded-lg p-4 bg-slate-700/30">
                    <h3 className="font-medium text-white mb-2">👢 Botas</h3>
                    {organizedItems.accessories.boots ? (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm text-white">{organizedItems.accessories.boots.item.name}</span>
                        </div>
                        <div className="text-xs text-purple-400">
                          Level: +{organizedItems.accessories.boots.enhancement}
                        </div>
                        {canManageCharacter && (
                          <div className="mt-2">
                            <Button
                              size="xs"
                              variant="secondary"
                              onClick={() => handleEditItem(organizedItems.accessories.boots)}
                            >
                              Editar
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <p className="text-gray-400 text-sm mb-2">Sem botas</p>
                        {canManageCharacter && (
                          <Button
                            size="xs"
                            onClick={() => openAddItemModal('BOOTS')}
                          >
                            Adicionar
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Luvas */}
                  <div className="border border-white/10 rounded-lg p-4 bg-slate-700/30">
                    <h3 className="font-medium text-white mb-2">🧤 Luvas</h3>
                    {organizedItems.accessories.gloves ? (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm text-white">{organizedItems.accessories.gloves.item.name}</span>
                        </div>
                        <div className="text-xs text-purple-400">
                          Level: +{organizedItems.accessories.gloves.enhancement}
                        </div>
                        {canManageCharacter && (
                          <div className="mt-2">
                            <Button
                              size="xs"
                              variant="secondary"
                              onClick={() => handleEditItem(organizedItems.accessories.gloves)}
                            >
                              Editar
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <p className="text-gray-400 text-sm mb-2">Sem luvas</p>
                        {canManageCharacter && (
                          <Button
                            size="xs"
                            onClick={() => openAddItemModal('GLOVES')}
                          >
                            Adicionar
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Bracelete */}
                  <div className="border border-white/10 rounded-lg p-4 bg-slate-700/30">
                    <h3 className="font-medium text-white mb-2">📿 Bracelete</h3>
                    {organizedItems.accessories.bracelet ? (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-sm text-white">{organizedItems.accessories.bracelet.item.name}</span>
                        </div>
                        <div className="text-xs text-purple-400">
                          Level: +{organizedItems.accessories.bracelet.enhancement}
                        </div>
                        {canManageCharacter && (
                          <div className="mt-2">
                            <Button
                              size="xs"
                              variant="secondary"
                              onClick={() => handleEditItem(organizedItems.accessories.bracelet)}
                            >
                              Editar
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <p className="text-gray-400 text-sm mb-2">Sem bracelete</p>
                        {canManageCharacter && (
                          <Button
                            size="xs"
                            onClick={() => openAddItemModal('BRACELET')}
                          >
                            Adicionar
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Relíquias */}
            <Card variant="glass">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">💎 Relíquias</h2>
                  {canManageCharacter && (
                    <Button
                      size="sm"
                      onClick={() => setShowAddRelicModal(true)}
                    >
                      Adicionar Relíquia
                    </Button>
                  )}
                </div>
                
                {character.relics && character.relics.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {character.relics.map((charRelic, index) => (
                      <div key={index} className="border border-white/10 rounded-lg p-4 bg-slate-700/30">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium text-white">{charRelic.relic.name}</h4>
                            <p className="text-sm text-gray-400">{charRelic.relic.description}</p>
                          </div>
                          <div className="text-right ml-4">
                            <span className="text-sm font-medium text-amber-400">
                              +{charRelic.relic.points} pts
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          Adiquirida em: {new Date(charRelic.acquiredAt).toLocaleDateString('pt-BR')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400 mb-4">Nenhuma relíquia encontrada</p>
                    {canManageCharacter && (
                      <Button
                        onClick={() => setShowAddRelicModal(true)}
                        size="sm"
                      >
                        Adicionar Primeira Relíquia
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Quests Completadas */}
            <Card variant="glass">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">🏆 Quests Completadas</h2>
                  {canManageCharacter && (
                    <Button
                      size="sm"
                      onClick={() => setShowCompleteQuestModal(true)}
                    >
                      Completar Quest
                    </Button>
                  )}
                </div>
                
                {character.quests && character.quests.length > 0 ? (
                  <div className="space-y-4">
                    {character.quests.map((quest, index) => (
                      <div key={index} className="border border-white/10 rounded-lg p-4 bg-slate-700/30">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium text-white">{quest.quest.name}</h4>
                            <p className="text-sm text-gray-400">{quest.quest.description}</p>
                          </div>
                          <div className="text-right ml-4">
                            <span className="text-sm font-medium text-amber-400">
                              +{quest.quest.points} pts
                            </span>
                          </div>
                        </div>
                        {quest.notes && (
                          <div className="mt-2 p-2 bg-slate-600/30 rounded text-sm text-gray-300">
                            <strong>Observações:</strong> {quest.notes}
                          </div>
                        )}
                        <div className="mt-2 text-xs text-gray-500">
                          Completada em: {new Date(quest.completedAt).toLocaleDateString('pt-BR')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400 mb-4">Nenhuma quest completada ainda</p>
                    {canManageCharacter && (
                      <Button
                        onClick={() => setShowCompleteQuestModal(true)}
                        size="sm"
                      >
                        Completar Primeira Quest
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddRelicModal
        isOpen={showAddRelicModal}
        onClose={() => setShowAddRelicModal(false)}
        onSubmit={handleAddRelic}
        availableRelics={availableRelics}
        loading={addingRelic}
      />

      <CompleteQuestModal
        isOpen={showCompleteQuestModal}
        onClose={() => setShowCompleteQuestModal(false)}
        onSubmit={handleCompleteQuest}
        availableQuests={availableQuests}
        loading={completingQuest}
      />

      <EditCharacterModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateCharacter}
        character={character}
        loading={editing}
        isAdmin={user?.role === 'ADMIN'}
      />

      <AddItemModal
        isOpen={showAddItemModal}
        onClose={closeAddItemModal}
        onSubmit={handleAddItem}
        availableItems={availableItems}
        itemTypeFilter={itemTypeFilter}
        loading={addingItem}
      />

      <EditItemModal
        isOpen={showEditItemModal}
        onClose={() => {
          setShowEditItemModal(false);
          setSelectedCharacterItem(null);
        }}
        onSubmit={handleUpdateItem}
        onRemove={handleRemoveItem}
        characterItem={selectedCharacterItem}
        loading={editingItem}
      />

      {/* Level Up Modal */}
      {showLevelUpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-slate-800 rounded-xl shadow-2xl max-w-md w-full p-6 border border-white/10"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Level Up!
              </h3>
              <p className="text-gray-300 text-lg">
                {character.name} subirá para o <strong className="text-yellow-400">Level {character.level + 1}</strong>
              </p>
            </div>

            <div className="bg-slate-700/50 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">Level Atual:</span>
                <span className="text-white font-semibold">{character.level}</span>
              </div>
              <div className="flex justify-between items-center text-sm mt-2">
                <span className="text-gray-400">Novo Level:</span>
                <span className="text-yellow-400 font-semibold">{character.level + 1}</span>
              </div>
            </div>

            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 font-semibold text-sm">Atenção!</p>
                  <p className="text-red-300 text-sm mt-1">
                    Esta ação não pode ser desfeita. Você ganhará pontos automaticamente baseado no novo level.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowLevelUpModal(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleLevelUp}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
              >
                {loading ? 'Processando...' : 'Confirmar Level Up'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Level Up Animation */}
      {showLevelUpAnimation && levelUpData && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none">
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 50 }}
            animate={{ 
              opacity: [0, 1, 1, 0],
              scale: [0.5, 1.2, 1.2, 1.5],
              y: [50, 0, 0, -100],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 3,
              times: [0, 0.3, 0.7, 1]
            }}
            className="text-center"
          >
            <div className="relative">
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 1.5,
                  repeat: 2,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full blur-3xl"
              />
              <div className="relative bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 rounded-2xl p-8 shadow-2xl border-4 border-yellow-300">
                <motion.div
                  animate={{
                    rotate: [0, 360],
                  }}
                  transition={{
                    duration: 2,
                    ease: "linear"
                  }}
                  className="text-8xl font-black text-white mb-4"
                  style={{
                    textShadow: '0 0 20px rgba(255,255,255,0.8), 0 0 40px rgba(255,215,0,0.6)'
                  }}
                >
                  GZZZZ!
                </motion.div>
                <div className="text-2xl font-bold text-white mb-2">
                  Level {levelUpData.newLevel}!
                </div>
                <div className="text-xl text-yellow-100">
                  +{levelUpData.pointsGained} Pontos
                </div>
              </div>
              
              {/* Partículas */}
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ 
                    opacity: 1,
                    scale: 0,
                    x: 0,
                    y: 0
                  }}
                  animate={{
                    opacity: [1, 0],
                    scale: [0, 1.5],
                    x: Math.cos((i * Math.PI * 2) / 12) * 150,
                    y: Math.sin((i * Math.PI * 2) / 12) * 150,
                  }}
                  transition={{
                    duration: 2,
                    delay: 0.3,
                    ease: "easeOut"
                  }}
                  className="absolute top-1/2 left-1/2 w-4 h-4 bg-yellow-400 rounded-full"
                  style={{
                    boxShadow: '0 0 10px rgba(255,215,0,0.8)'
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </PageContainer>
  );
};

export default CharacterDetail;